import React, { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import SimpleHeader from '../layouts/SimpleHeader'
import SideMenu from '../layouts/SideMenu'
import MenuWrap from '../components/BurgerMenu/MenuWrap'
import ValidateEmail from '../components/EmailValidation'
import { ADD_USER_QUERY, GET_USERDATA_QUERY } from '../services/GraphQLQueries'
import LoadingPage from './LoadingPage'

const AdminAddUser = () => {
  const [addUser, { loading }] = useMutation(ADD_USER_QUERY)
  const { data, loading: userLoading } = useQuery(GET_USERDATA_QUERY, {
    fetchPolicy: 'no-cache',
  })
  const [formValues, setFormValues] = useState({
    fullName: '',
    email: '',
    canUseMagicLink: true,
  })
  const [formErrors, setFormErrors] = useState({})
  const [resultMessage, setResultMessage] = useState('')
  const [tempPassword, setTempPassword] = useState('')
  const [magicLinkUrl, setMagicLinkUrl] = useState('')
  const signInUrl = useMemo(
    () => (typeof window !== 'undefined' ? `${window.location.origin}/signin` : ''),
    []
  )

  const formTitle = useMemo(() => 'Admin: Add New User', [])

  const generateTempPassword = () =>
    `Temp-${Math.random().toString(36).slice(2, 10)}-${Date.now()
      .toString(36)
      .slice(-4)}`

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value,
    })
  }

  const validateForm = () => {
    const errors = {}
    if (!formValues.fullName.trim()) {
      errors.fullName = 'Please enter the user\'s name.'
    }
    if (!ValidateEmail(formValues.email)) {
      errors.email = 'Please enter a valid email address.'
    }
    return errors
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setResultMessage('')
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    setFormErrors({})
    const generatedPassword = generateTempPassword()
    setTempPassword(generatedPassword)

    try {
      const response = await addUser({
        variables: {
          fullname: formValues.fullName.trim(),
          email: formValues.email.trim(),
          password: generatedPassword,
          canUseMagicLink: formValues.canUseMagicLink,
        },
      })

      if (response.data?.addUser?.error) {
        setResultMessage(response.data.addUser.error)
        setMagicLinkUrl('')
      } else {
        setResultMessage('User added successfully. Share the temporary password below to help them sign in.')
        debugger;
        setMagicLinkUrl(response.data?.addUser?.magicLinkUrl ?? '')
        setFormValues({ fullName: '', email: '', canUseMagicLink: true })
      }
    } catch (error) {
      setResultMessage('Something went wrong while adding the user. Please try again.')
      setMagicLinkUrl('')
    }

    console.log('Submitting vars:', {
      fullname: formValues.fullName.trim(),
      email: formValues.email.trim(),
      password: generatedPassword,
      canUseMagicLink: formValues.canUseMagicLink,
    })
  }

  if (userLoading) {
    return <LoadingPage />
  }

  const isAdmin = data?.userdata?.admin
  
  return (
    <div id="outer-container" style={{ height: '100%' }}>
      <MenuWrap className="sm:hidden">
        <SideMenu isAuthorized={true}></SideMenu>
      </MenuWrap>
      <div id="page-wrap">
        <div className="flex">
          <SideMenu isAuthorized={true} className="hidden sm:block"></SideMenu>
          <div className="w-full">
            <SimpleHeader />
            <div className="min-h-[calc(100vh-175px)] bg-gradient-to-br from-white via-[#f8fbf9] to-[#eef6f1] px-6 py-12">
              <div className="max-w-3xl mx-auto">
                <div className="rounded-3xl border border-[#dbe7df] bg-white/90 p-10 shadow-xl">
                  {!isAdmin ? (
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#6f8a79]">
                        Admin access required
                      </p>
                      <h1 className="text-3xl font-semibold text-[#1f2b23] mt-3">
                        You don&apos;t have permission to add users.
                      </h1>
                      <p className="text-sm text-[#6d7b73] mt-4">
                        Please contact an administrator if you need access to this tool.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-8">
                        <p className="text-xs uppercase tracking-[0.2em] text-[#6f8a79]">
                          Manual user management
                        </p>
                        <h1 className="text-3xl font-semibold text-[#1f2b23] mt-2">
                          {formTitle}
                        </h1>
                        <p className="text-sm text-[#6d7b73] mt-3">
                          Add a new team member by entering their name and email. We&apos;ll generate a temporary
                          password for them.
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-[#1f2b23]" htmlFor="fullName">
                            Full name
                          </label>
                          <input
                            id="fullName"
                            name="fullName"
                            type="text"
                            value={formValues.fullName}
                            onChange={handleChange}
                            placeholder="Jordan Smith"
                            className="mt-2 w-full rounded-2xl border border-[#dbe7df] bg-[#f6faf7] px-4 py-3 text-base text-[#1f2b23] shadow-sm focus:border-[#86b194] focus:outline-none focus:ring-2 focus:ring-[#c7e0cf]"
                          />
                          {formErrors.fullName ? (
                            <p className="mt-2 text-sm font-medium text-[#d34f4f]">
                              {formErrors.fullName}
                            </p>
                          ) : null}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[#1f2b23]" htmlFor="email">
                            Email address
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={formValues.email}
                            onChange={handleChange}
                            placeholder="jordan@company.com"
                            className="mt-2 w-full rounded-2xl border border-[#dbe7df] bg-[#f6faf7] px-4 py-3 text-base text-[#1f2b23] shadow-sm focus:border-[#86b194] focus:outline-none focus:ring-2 focus:ring-[#c7e0cf]"
                          />
                          {formErrors.email ? (
                            <p className="mt-2 text-sm font-medium text-[#d34f4f]">
                              {formErrors.email}
                            </p>
                          ) : null}
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full rounded-2xl bg-[#1f2b23] px-6 py-3 text-base font-semibold tracking-wide text-white shadow-lg transition hover:bg-[#28372d] disabled:cursor-not-allowed disabled:bg-[#94a39a]"
                        >
                          {loading ? 'Adding user...' : 'Add user'}
                        </button>
                        <div className="flex items-center justify-between rounded-2xl border border-[#dbe7df] bg-[#f6faf7] px-4 py-3">
                          <div>
                            <p className="text-sm font-semibold text-[#1f2b23]">Enable magic link sign-in</p>
                            <p className="text-xs text-[#6d7b73]">
                              Allows the new user to authenticate via a magic link.
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="canUseMagicLink"
                              checked={formValues.canUseMagicLink}
                              onChange={handleChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-[#dbe7df] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#c7e0cf] rounded-full peer peer-checked:bg-[#1f2b23] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-[#dbe7df] after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                        </div>
                      </form>

                      {resultMessage ? (
                        <div className="mt-6 rounded-2xl border border-[#dbe7df] bg-[#f3f7f4] px-5 py-4 text-sm text-[#1f2b23]">
                          <p>{resultMessage}</p>
                          {tempPassword && !loading ? (
                            <p className="mt-3 font-semibold text-[#1f2b23]">
                              Temporary password: <span className="font-mono">{tempPassword}</span>
                            </p>
                          ) : null}
                          {signInUrl && !loading ? (
                            <p className="mt-3 font-semibold text-[#1f2b23]">
                              {magicLinkUrl ? (
                                <>
                                  Magic link URL:{' '}
                                  <a className="font-mono text-[#1f2b23] underline" href={magicLinkUrl}>
                                    {magicLinkUrl}
                                  </a>
                                </>
                              ) : (
                                <>
                                  Sign-in URL:{' '}
                                  <a className="font-mono text-[#1f2b23] underline" href={signInUrl}>
                                    {signInUrl}
                                  </a>
                                </>
                              )}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAddUser
